import React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { twMerge } from "tailwind-merge";

const buttonVariants = cva(
  "inline-flex items-center gap-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none transition-all duration-200 font-medium justify-center [&.text-left]:justify-start",
  {
    variants: {
      variant: {
        solid: "border border-transparent",
        outline: "bg-transparent border border-gray-300 dark:border-gray-600",
        ghost: "bg-transparent border-transparent hover:bg-gray-100",
      },
      size: {
        xs: "text-xs px-2 py-1",
        sm: "text-sm px-3 py-1.5",
        md: "text-base px-4 py-2",
        lg: "text-lg px-6 py-3",
        xl: "text-xl px-8 py-4",
        icon: "p-1",
      },
      rounded: {
        sm: "rounded",
        md: "rounded-md",
        lg: "rounded-lg",
        xl: "rounded-xl",
        "2xl": "rounded-2xl",
        full: "rounded-full",
      },
      fullWidth: {
        true: "w-full",
        false: "w-max",
      },
      borderWidth: {
        thin: "border",
        medium: "border-2",
        thick: "border-4",
      },
    },
    // defaultVariants: {
    //   variant: "solid",
    //   size: "md",
    //   rounded: "md",
    //   fullWidth: false,
    // },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  bgColor?: string;
  textColor?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  borderColor?: string;
  loading?: boolean;
  disableFullWidth?: boolean;
  "data-testid"?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className = "",
      variant,
      size,
      rounded,
      bgColor,
      textColor,
      leftIcon,
      rightIcon,
      loading = false,
      fullWidth,
      disableFullWidth = false,
      borderColor,
      borderWidth,
      "data-testid": dataTestId,
      children,
      ...props
    },
    ref,
  ) => {
    const widthValue = disableFullWidth ? false : fullWidth;

    return (
      <button
        ref={ref}
        data-testid={dataTestId}
        className={twMerge(
          buttonVariants({
            variant,
            size,
            rounded,
            fullWidth: widthValue,
            borderWidth,
          }),
          bgColor,
          textColor,
          className,
          borderColor,
        )}
        disabled={props.disabled || loading}
        {...props}
      >
        {loading ? (
          <svg
            className="animate-spin h-5 w-5"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
        ) : (
          <>
            {leftIcon && <span className="inline-flex">{leftIcon}</span>}
            {children}
            {rightIcon && <span className="inline-flex">{rightIcon}</span>}
          </>
        )}
      </button>
    );
  },
);

Button.displayName = "Button";

export { Button, buttonVariants };
